import type { AgentBlock, ConversationMessage } from "../api/types";
import type { StreamPart, TurnState } from "./agentStore";
import { blockType } from "./registry";

/*
  The plain parts of the chat bar's log, kept apart from the screen code so they can be tested alone. The panel in
  AgentPanel.tsx does the same work with its own copies of these; they are kept in step by hand until that panel goes.
*/

/** A message the person sent that the stored conversation does not show yet. */
export interface PendingMessage {
  conversationKey: string;
  text: string;
  /** The highest message number the screen knew when the message was sent. */
  afterSeq: number;
}

/** The words of a stored message, for matching it with a message that is still on its way. */
export function wordsOf(message: ConversationMessage): string {
  return message.display
    .map((block) => (blockType(block) === "text" ? (block as { text: string }).text : ""))
    .join("")
    .trim();
}

/** The messages sent that the stored conversation has not caught up with: the person's own words show at once and not twice. */
export function stillWaiting(pending: readonly PendingMessage[], conversationKey: string | null, messages: readonly ConversationMessage[]): PendingMessage[] {
  return pending.filter(
    (item) => item.conversationKey === conversationKey && !messages.some((m) => m.role === "user" && m.seq > item.afterSeq && wordsOf(m) === item.text),
  );
}

export type Group =
  | { kind: "text"; seq: number; text: string }
  | { kind: "block"; seq: number; block: AgentBlock }
  | { kind: "tool"; seq: number; part: Extract<StreamPart, { kind: "tool" }> };

/** Put the pieces of a turn in order, joining the words that follow each other. */
export function groupParts(turn: TurnState): Group[] {
  const groups: Group[] = [];
  for (const part of turn.parts) {
    if (part.kind === "text") {
      const last = groups[groups.length - 1];
      if (last?.kind === "text") last.text += part.text;
      else groups.push({ kind: "text", seq: part.seq, text: part.text });
    } else if (part.kind === "block") {
      groups.push({ kind: "block", seq: part.seq, block: part.block });
    } else {
      groups.push({ kind: "tool", seq: part.seq, part });
    }
  }
  return groups;
}

/** What the agent is doing, in plain words, while a tool runs and has no summary yet. */
const TOOL_WORDS: Record<string, string> = {
  explain: "Writing an explanation",
  ask: "Writing questions for you",
  show_plan: "Writing what it will do",
  read_deliverable: "Reading a document",
  find_in_ontology: "Searching this discipline",
  find_in_library: "Searching the libraries",
  propose: "Drafting proposal cards",
  point_to: "Pointing to a screen",
  run_checks: "Running the checks",
  run_coverage: "Running the coverage check",
  make_restore_point: "Making a restore point",
  log_note: "Writing a note in the log",
  remember: "Updating what it remembers",
  prepare_hand_over: "Preparing a hand-over",
  start_practice: "Opening the practice space",
  open_skill_topic: "Reading its guidance",
};

/** The line shown for a tool: its summary, or the plain words for the tool while it runs. */
export function toolWord(part: { name: string; summary?: string | null }): string {
  return part.summary || TOOL_WORDS[part.name] || "A step";
}

/** What to say when a turn did not end well. */
export const END_SENTENCES: Record<string, string> = {
  cancelled: "You stopped the agent's answer.",
  interrupted: "The agent was interrupted. You can ask again.",
  limit: "The agent reached a limit for one answer. What it did is saved.",
  error: "The agent could not finish its answer.",
};
